import React from 'react';
import { ChevronRight } from 'lucide-react';
import SeverityBadge from './SeverityBadge';
import CoverageSourcePills from './CoverageSourcePills';

export default function GovernanceStatement({
  severity = 'medium',
  statement,
  detail,
  evidenceCount,
  controls,
  onPrimary,
  primaryLabel = 'View evidence',
  secondaryLabel,
  onSecondary,
  onCardClick,
  highlighted = false,
  testId,
  titleSuffix = null,
}) {
  const handleKey = (e) => {
    if (!onCardClick) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCardClick(); }
  };
  return (
    <div
      data-testid={testId}
      onClick={onCardClick}
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      onKeyDown={onCardClick ? handleKey : undefined}
      className={`group relative bg-white rounded-lg border transition-all hover:shadow-md ${onCardClick ? 'cursor-pointer hover:-translate-y-0.5 hover:border-apex-lens/40' : ''} ${highlighted ? 'border-apex-lens ring-1 ring-apex-lens/20' : 'border-slate-200'} p-4 sm:p-5`}
    >
      {/* Right-edge accent rail — appears on hover to telegraph the card is clickable */}
      {onCardClick && (
        <div className="absolute right-0 top-2 bottom-2 w-0.5 rounded-l-full bg-apex-lens scale-y-0 origin-center group-hover:scale-y-100 transition-transform duration-200" />
      )}
      <div className="flex items-start gap-3">
        <SeverityBadge severity={severity} />
        <div className="flex-1 min-w-0">
          <p className="text-[15px] leading-snug font-semibold text-apex-ink">
            {statement}
            {titleSuffix && <span className="inline-block align-middle ml-1.5">{titleSuffix}</span>}
          </p>
          {detail && (
            <p className="mt-1.5 text-xs text-apex-neutral leading-relaxed">{detail}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {evidenceCount !== undefined && (
              <span className="text-[11px] font-mono text-apex-neutral">
                {evidenceCount} CloudTrail events
                {controls ? ` · ${controls} framework controls` : ''}
              </span>
            )}
            <CoverageSourcePills />
          </div>
          <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => onCardClick && e.stopPropagation()}>
            {onPrimary && (
              <button
                onClick={(e) => { e.stopPropagation(); onPrimary(); }}
                data-testid={`${testId}-primary`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-apex-lens hover:text-apex-lens-dark transition-colors"
              >
                {primaryLabel}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            {onSecondary && (
              <button
                onClick={(e) => { e.stopPropagation(); onSecondary(); }}
                data-testid={`${testId}-secondary`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-apex-ink-2 hover:text-apex-ink transition-colors"
              >
                {secondaryLabel}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            {onCardClick && (
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-apex-neutral group-hover:text-apex-lens transition-colors">
                Open detail
                <ChevronRight className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
